import { Component, Inject, ChangeDetectionStrategy } from '@angular/core'
import {
    Action,
    arrayComparatorFactory,
    booleanComparator,
    compareDates,
    ConfirmationService,
    Entry,
    ErrorsT,
    FieldTracker,
    FluentTracker,
    im,
    isPositiveInt,
    isZeroOrPositiveInt,
    LabelProvider,
    libArrayValuesToMap,
    LibraryHelper,
    libValuesToMap,
    selfValuesFactory,
    SimpleControlDelegateProvider,
    SmartColumns,
    stringToMap,
    Table,
    TableControlType,
    TableDelegate,
    TableExportType,
    Via
} from '@surgetech-eu/core'
import moment from 'moment'
import uuid from 'uuid'
import { Observable, of, Subscriber } from 'rxjs'
import { map, mergeMap, tap } from 'rxjs/operators'
import { LibraryAttributeWithDescription, Suppliers, SuppliersService } from '../services/suppliers.service'
import { CountryService } from '../services/country.service'
import { SupplierType, SupplierTypeService } from '../services/supplier-type.service'
import { disabledColumnOptions, galvanicTreatmentOptions, maxRank, semifinishedSupplierOptions } from '../constant'
import { OrganizationInfo, OrganizationsService } from '../services/organizations.service'
import { SortRank } from '../helpers'
import { ActivatedRoute } from '@angular/router'

export enum columnName {
    key = 'key',
    sapCode = 'sapCode',
    country = 'country',
    description = 'description',
    supplierStatus = 'supplierStatus',
    types = 'types',
    catalogUploadedBy = 'catalogUploadedBy',
    hasVisibilityRules = 'hasVisibilityRules',
    visibleTo = 'visibleTo',
    semiFinishedSupplier = 'semiFinishedSupplier',
    rank = 'rank'
}

@Component({
    selector: 'app-suppliers',
    templateUrl: './suppliers.component.html',
    styleUrls: ['./suppliers.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
@SmartColumns()
export class SuppliersComponent implements TableDelegate {
    private table: Table

    saving = false
    saved = false

    error: ErrorsT = null

    private fluentTracker: FluentTracker
    private pendingSource: Suppliers[] = []
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private countrySource: any[] = []
    private supplierTypeSource: SupplierType[] = []
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private organizationsSource: any[] = []
    private supplierCountryValue: Map<string, string> = new Map<string, string>()
    private supplierTypeValue: Map<string, string> = new Map<string, string>()
    private catalogueUploadedByValue: Map<string, string> = new Map<string, string>()
    private visibleToValue: Map<string, string> = new Map<string, string>()
    private labels: LabelProvider
    private delegates: { [type: string]: SimpleControlDelegateProvider } = {
        catalogUploadedBy: new SimpleControlDelegateProvider(),
        visibleTo: new SimpleControlDelegateProvider(),
        key: new SimpleControlDelegateProvider()
    }

    importUrl = '/libraries/management/types/supplier/import-entries'

    constructor(
        @Inject('surge.core.schema-transformers') private schemaTransformers: any[],
        private suppliersService: SuppliersService,
        private countryService: CountryService,
        private supplierTypeService: SupplierTypeService,
        private confirmationService: ConfirmationService,
        private organizationsService: OrganizationsService,
        route: ActivatedRoute
    ) {
        const data = route.snapshot.data
        this.labels = data.labels
        this.fluentTracker = new FluentTracker(new FieldTracker(), {
            fields: {
                id: {
                    ns: true
                },
                organizationId: {
                    required: true
                },
                key: {
                    required: true,
                    validate: (value, extra, withinNs) => {
                        if (!value) {
                            return "It's required"
                        }
                        if (
                            this.pendingSource.findIndex(
                                item => item.key.trim() === value.trim() && item.id.trim() !== withinNs.trim()
                            ) !== -1
                        ) {
                            return 'Should have a unique value'
                        }
                        return null
                    }
                },
                sapCode: {
                    required: false
                },
                country: {
                    required: true,
                    validate: (v, extra, withinNs) => {
                        if (!v) {
                            return "It's required"
                        }
                        if (v && typeof v === 'string') {
                            const findT = this.countrySource.find(
                                tItem => tItem.key?.toLowerCase() === v?.toLowerCase()
                            )
                            if (!this.supplierCountryValue.get(v?.toLowerCase()) && !findT) {
                                return im('{value} is not found', { value: v })
                            }
                        }
                        if (v && typeof v === 'object') {
                            const findT = this.countrySource.find(
                                tItem => tItem.key?.toLowerCase() === v.id?.toLowerCase()
                            )
                            if (!findT) {
                                return im('{id} - {code} is not found', { id: v?.id, code: v?.code })
                            }
                        }
                        return null
                    }
                },
                description: {
                    required: true
                },
                supplierStatus: {
                    required: false
                },
                types: {
                    required: true,
                    validate: (v, extra, withinNs) => {
                        const errors: ErrorsT = []
                        if (!v?.length) {
                            return "It's required"
                        }
                        v.map(item => {
                            if (item && typeof item === 'object') {
                                if (!this.supplierTypeValue.get(item.id?.toLowerCase())) {
                                    errors.push(LibraryHelper.libraryAttributeToStringOption(item))
                                }
                            }
                        })
                        if (errors.length) {
                            errors.push('not found')
                            return errors
                        }
                        return null
                    }
                },
                catalogUploadedBy: {
                    required: true,
                    validate: (v, extra, withinNs) => {
                        if (!v) {
                            return "It's required"
                        }
                        if (v && typeof v === 'string') {
                            if (
                                v.toLowerCase() === 'none' &&
                                !this.fluentTracker.tracker.getValues(withinNs).supplierStatus
                            ) {
                                return null
                            }
                            if (!this.catalogueUploadedByValue.get(v?.toLowerCase())) {
                                return im('{value} is not found', { value: v })
                            }
                        }
                        if (v && typeof v === 'object') {
                            if (!this.catalogueUploadedByValue.get(v.id?.toLowerCase())) {
                                return im('{value} is not found', { value: LibraryHelper.libraryAttributeToStringOption(v) })
                            }
                        }
                        return null
                    }
                },
                hasVisibilityRules: {
                    required: true,
                    validate: (v, extra, withinNs) => {
                        if (typeof v !== 'boolean') {
                            return "It's required"
                        }
                        return null
                    }
                },
                semiFinishedSupplier: {
                    required: false,
                    validate: (v, extra, withinNs) => {
                        if (v && typeof v !== 'boolean') {
                            return 'Invalid Value'
                        }
                        return null
                    }
                },
                visibleTo: {
                    required: true,
                    validate: (v, extra, withinNs) => {
                        if (!v?.length) {
                            return "It's required"
                        }
                        const errors = []
                        const row = this.fluentTracker.tracker.getValues(withinNs)
                        if (row?.hasVisibilityRules) {
                            v.forEach(toError => {
                                const existsInCollection = this.visibleToValue.get(toError?.id?.toLowerCase())
                                if (!existsInCollection) {
                                    errors.push(LibraryHelper.libraryAttributeToStringOption(toError))
                                }
                            })
                        }
                        if (errors.length) {
                            errors.push('not found')
                            return errors
                        }
                        return null
                    }
                },
                rank: {
                    required: false,
                    validate: value => {
                        if (value === null) {
                            return null
                        }
                        if (!isPositiveInt(value)) {
                            return 'Should be a positive number'
                        }
                        if (value > maxRank) {
                            return im('It must be less than {maxRank}', { maxRank })
                        }
                        return null
                    }
                },
                disabled: {
                    required: false
                },
                library: {
                    required: true
                },
                namespace: {
                    required: true
                },
                new: {
                    required: false
                },
                fieldsToReset: {
                    required: false
                }
            }
        })
        this.fluentTracker.listen(FluentTracker.EVENT_CHANGE, () => (this.error = null), true)
    }

    getTableData(): Observable<Suppliers[]> {
        if (this.pendingSource.length) {
            return of(this.pendingSource)
        }
        return this.organizationsService
            .getOrganizations()
            .pipe(
                mergeMap(organizations => {
                    this.organizationsSource = Object.entries(organizations)
                        .map(([key, value]) => value)
                        .reduce((acc, v) => [...acc, ...v])
                    this.organizationsSource.forEach(organization => {
                        this.catalogueUploadedByValue.set(
                            organization.companyId?.toLowerCase(),
                            organization.companyName
                        )
                    })
                    return this.countryService.getAll()
                })
            )
            .pipe(
                mergeMap(countries => {
                    this.countrySource = countries
                    countries.map(item => {
                        this.supplierCountryValue.set(
                            item.key?.toLowerCase(),
                            item.description ? item.key + ' - ' + item.description : item.key
                        )
                    })
                    return this.supplierTypeService.getAll()
                })
            )
            .pipe(
                mergeMap(supplierTypes => {
                    this.supplierTypeSource = supplierTypes
                    supplierTypes.filter(x => !x.disabled).map(item => {
                        this.supplierTypeValue.set(
                            item.key?.toLowerCase(),
                            item.description ? item.key + ' - ' + item.description : item.key
                        )
                    })
                    return this.suppliersService.getAll()
                })
            )
            .pipe(
                map(data =>
                    data.map((suppliers: Suppliers) => {
                        this.visibleToValue.set(suppliers.organizationId?.toLowerCase(), suppliers.key)
                        let visibleTo: LibraryAttributeWithDescription[] = suppliers.visibleTo.map(id => {
                            const findCompany = this.organizationsSource.find(
                                organization => organization.companyId === id
                            )
                            if (findCompany) {
                                return {
                                    id: findCompany.companyId,
                                    code: findCompany.attributes?.vatCode,
                                    companyName: this.getCompanyInfo(findCompany.companyId)?.companyName
                                }
                            }
                        })
                        if (!visibleTo.length) {
                            visibleTo = [
                                {
                                    id: 'all',
                                    code: 'ALL'
                                }
                            ]
                        }
                        if (!suppliers.catalogUploadedBy && !suppliers?.organization?.active) {
                            suppliers.catalogUploadedBy = 'none'
                        }
                        return {
                            ...suppliers,
                            visibleTo,
                            supplierStatus: suppliers?.organization?.active,
                            organizationId: suppliers?.organization?.companyId
                        }
                    })
                ),
                tap(data => (this.pendingSource = [...data]))
            )
    }

    onTableDelegateBind(table: Table): void {
        this.table = table
    }

    disableSupportQuery(): boolean {
        return true
    }

    getColumnsConfig() {
        return [
            {
                name: columnName.key,
                title: this.labels.get('libraries.supplierTable.key'),
                type: 'text'
            },
            {
                name: columnName.sapCode,
                title: this.labels.get('libraries.supplierTable.sapCode'),
                type: 'text'
            },
            {
                name: columnName.country,
                title: this.labels.get('libraries.supplierTable.country'),
                type: 'text'
            },
            {
                name: columnName.description,
                title: this.labels.get('libraries.supplierTable.description'),
                type: 'text'
            },
            {
                name: columnName.types,
                title: this.labels.get('libraries.supplierTable.types'),
                type: 'text'
            },
            {
                name: columnName.supplierStatus,
                title: this.labels.get('libraries.supplierTable.supplierStatus'),
                type: 'text'
            },
            {
                name: columnName.catalogUploadedBy,
                title: this.labels.get('libraries.supplierTable.catalogUploadedBy'),
                type: 'text'
            },
            {
                name: columnName.hasVisibilityRules,
                title: this.labels.get('libraries.supplierTable.hasVisibilityRules'),
                type: 'boolean'
            },
            {
                name: columnName.visibleTo,
                title: this.labels.get('libraries.supplierTable.visibleTo'),
                type: 'text'
            },
            {
                name: columnName.semiFinishedSupplier,
                title: this.labels.get('libraries.supplierTable.semifinished'),
                type: 'boolean'
            },
            {
                name: 'createdAt',
                title: this.labels.get('libraries.supplierTable.createdAt'),
                type: 'date'
            },
            {
                name: 'createdBy',
                title: this.labels.get('libraries.supplierTable.createdBy'),
                type: 'text'
            },
            {
                name: 'updatedAt',
                title: this.labels.get('libraries.supplierTable.updatedAt'),
                type: 'date'
            },
            {
                name: 'updatedBy',
                title: this.labels.get('libraries.supplierTable.updatedBy'),
                type: 'text'
            },
            {
                name: columnName.rank,
                title: this.labels.get('libraries.supplierTable.rank')
            },
            {
                name: 'disabled',
                title: this.labels.get('libraries.supplierTable.disabled')
            }
        ]
    }

    customizeColumns(columnsMap) {
        const keyColumn = columnsMap.get(columnName.key)
        keyColumn.control = {
            delegate: (value, row) => this.delegates.key.get(row.id),
            type: TableControlType.INPUT,
            changing: (value, row) => {
                const company = this.getCompany(value)
                const companyInfo = this.getCompanyInfo(company?.companyId)
                this.updateFluentValue(value, row, columnName.key)
                if (companyInfo) {
                    if (companyInfo.active) {
                        this.delegates.catalogUploadedBy
                            .get(row.id)
                            .coordinator.changeValue(companyInfo.companyId, false)
                    }
                    this.updateFluentValue(company.attributes?.sapCode, row, columnName.sapCode)
                    this.updateFluentValue(companyInfo.active, row, columnName.supplierStatus)
                    this.updateFluentValue(companyInfo.companyName, row, columnName.description)
                    this.updateFluentValue(company.companyId, row, 'organizationId')
                } else {
                    this.fluentTracker.tracker.addError(row.id, columnName.key, 'not found')
                    this.delegates.catalogUploadedBy.get(row.id).coordinator.changeValue(null, false)
                    this.updateFluentValue(null, row, columnName.sapCode)
                    this.updateFluentValue(null, row, columnName.supplierStatus)
                    this.updateFluentValue(null, row, columnName.description)
                    this.updateFluentValue(null, row, 'organizationId')
                }
            },
            canShow: (value, row) => row.new,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.key),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.key),
            seed: row => {
                return this.fluentTracker.tracker.getValue(row.id, columnName.key) || row.key
            }
        }
        keyColumn.filter = {
            via: Via.TEXT
        }

        const sapCodeColumn = columnsMap.get(columnName.sapCode)
        sapCodeColumn.control = {
            type: TableControlType.INPUT,
            disabled: (value, row) => {
                return row.organizationId && value
            },
            changing: (value, row) => {
                this.updateFluentValue(value, row, columnName.sapCode)
            },
            canShow: (value, row) => row.new,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.sapCode),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.sapCode),
            seed: row => {
                return this.fluentTracker.tracker.getValue(row.id, columnName.sapCode) || row.sapCode
            }
        }

        const countryColumn = columnsMap.get(columnName.country)
        countryColumn.filter = {
            via: Via.SELF,
            selfValues: selfValuesFactory('country', libValuesToMap),
            path: 'country.id'
        }
        countryColumn.control = {
            type: TableControlType.SELECT,
            values: (v, row) => {
                const options = new Map<string, string>()
                this.countrySource.map(item => {
                    options.set(item.key, item.description ? item.key + ' - ' + item.description : item.key)
                })
                if (v && !this.supplierCountryValue.get(v?.id)) {
                    options.set(v.id, v.code ? v.id + ' - ' + v.code : v.id)
                }
                return options
            },
            change: (value, row) => {
                const find = this.countrySource.find(item => item.key === value)
                if (find) {
                    value = {
                        id: find.key,
                        code: find.description
                    }
                }
                this.updateFluentValue(value, row, columnName.country)
            },
            canShow: (value, row) => row.new,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.country),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.country),
            seed: (row: Suppliers) => {
                return (
                    this.fluentTracker.tracker.getValue(row.id, columnName.country)?.id?.toLowerCase() ||
                    row.country?.id?.toLowerCase()
                )
            }
        }
        countryColumn.pipe = (value, row) => {
            if (row.new) {
                return value
            }
            return this.supplierCountryValue.get(value?.id?.toLowerCase())
        }

        const supplierNameColumn = columnsMap.get(columnName.description)
        supplierNameColumn.control = {
            type: TableControlType.INPUT,
            disabled: (value, row) => {
                return row.organizationId && value
            },
            changing: (value, row) => {
                this.updateFluentValue(value, row, columnName.description)
            },
            canShow: () => true,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.description),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.description),
            seed: row => {
                return this.fluentTracker.tracker.getValue(row.id, columnName.description) || row.description
            }
        }

        const supplierTypeColumn = columnsMap.get(columnName.types)
        supplierTypeColumn.control = {
            type: TableControlType.SELECT,
            values: (value, row) => {
                const options = new Map<string, string>()
                this.supplierTypeSource.sort(SortRank).map(item => {
                    if (
                        !item.disabled ||
                        row.types?.find(findItem => findItem.id?.toLowerCase() === item.key?.toLowerCase())
                    ) {
                        options.set(
                            item.key?.toLowerCase(),
                            item.description ? item.key + ' - ' + item.description : item.key
                        )
                    }
                })
                value?.forEach(item => {
                    if (item && !this.supplierTypeValue.get(item?.id?.toLowerCase())) {
                        options.set(item.id?.toLowerCase(), item.code ? item.id + ' - ' + item.code : item.id)
                    }
                })
                return options
            },
            multiple: true,
            change: (value, row) => {
                value = value?.map(valueItem => {
                    const find = this.supplierTypeSource.find(
                        item => item.key.toLowerCase() === valueItem.toLowerCase()
                    )
                    if (find) {
                        return {
                            id: find.key,
                            code: find.description
                        }
                    } else {
                        return {
                            id: row.types?.find(item => item.id?.toLowerCase() === valueItem?.toLowerCase())?.id,
                            code: row.types?.find(item => item.id?.toLowerCase() === valueItem?.toLowerCase())?.code
                        }
                    }
                })
                if (value?.length) {
                    this.updateFluentValue(value, row, columnName.types)
                } else {
                    this.updateFluentValue(null, row, columnName.types)
                }
            },
            canShow: (value, row) => true,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.types),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.types),
            seed: (row: Suppliers) => {
                return (
                    this.fluentTracker.tracker
                        .getValue(row.id, columnName.types)
                        ?.map(item => item.id?.toLowerCase()) || row.types?.map(item => item.id?.toLowerCase())
                )
            }
        }
        supplierTypeColumn.filter = {
            via: Via.SELFPICKER,
            selfValues: selfValuesFactory('types', libArrayValuesToMap),
            comparator: arrayComparatorFactory(v => (v ? v.map(item => item.id) : []))
        }

        const supplierStatusColumn = columnsMap.get('supplierStatus')
        supplierStatusColumn.pipe = value => {
            return value ? 'Active in BC' : 'Not Active in BC'
        }
        supplierStatusColumn.filter = {
            via: Via.DROPDOWN,
            values: new Map([
                ['true', 'Active'],
                ['false', 'Not Active']
            ]),
            comparator: booleanComparator
        }

        const uploadedByColumn = columnsMap.get(columnName.catalogUploadedBy)
        uploadedByColumn.control = {
            delegate: (value, row) => this.delegates.catalogUploadedBy.get(row.id),
            disabled: (value, row: Suppliers) => {
                return !!row.organizationId && !!value && row.supplierStatus
            },
            type: TableControlType.SELECT,
            values: (value, row: Suppliers) => {
                let options = new Map<string, string>()
                if (!row.supplierStatus) {
                    options = new Map<string, string>([
                        ['none', 'None'],
                        ['kering', 'Kering']
                    ])
                } else {
                    this.organizationsSource.map(item => {
                        options.set(item.companyId, item.companyName)
                    })
                }
                if (
                    value &&
                    typeof value === 'object' &&
                    !this.catalogueUploadedByValue.get(value?.id?.toLowerCase())
                ) {
                    options.set(value.id, value.code)
                } else if (
                    value &&
                    typeof value === 'string' &&
                    !this.catalogueUploadedByValue.get(value?.toLowerCase()) &&
                    !options.get(value?.toLowerCase())
                ) {
                    options.set(value, value)
                }
                return options
            },
            change: (value, row) => {
                const find = this.organizationsSource.find(
                    item => item.companyId?.toLowerCase() === value?.toLowerCase()
                )
                if (find) {
                    value = {
                        id: find.companyId,
                        code: find.companyName
                    }
                }
                this.updateFluentValue(value, row, columnName.catalogUploadedBy)
            },
            canShow: (value, row) => true,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.catalogUploadedBy),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.catalogUploadedBy),
            seed: row => {
                return (
                    this.fluentTracker.tracker.getValue(row.id, columnName.catalogUploadedBy)?.id ||
                    row.catalogUploadedBy
                )
            }
        }
        uploadedByColumn.filter = {
            via: Via.SELF,
            selfValues: selfValuesFactory('catalogUploadedBy', (values, transformer) => {
                if (values) {
                    const companyName = this.catalogueUploadedByValue.get(values)
                    if (companyName) {
                        return {
                            id: values,
                            value: companyName
                        }
                    } else {
                        return {
                            id: values,
                            value: values
                        }
                    }
                }
                return null
            })
        }

        const visibilityRulesColumn = columnsMap.get(columnName.hasVisibilityRules)
        visibilityRulesColumn.control = {
            type: TableControlType.SELECT,
            values: new Map<string, string>([
                ['true', 'Yes'],
                ['false', 'No']
            ]),
            change: (value, row) => {
                if (value !== null) {
                    value = value === 'true'
                }
                this.updateFluentValue(value, row, columnName.hasVisibilityRules)
                if (value !== true) {
                    this.delegates.visibleTo.get(row.id).coordinator.changeValue(['all'], false)
                } else {
                    this.delegates.visibleTo.get(row.id).coordinator.changeValue(null, false)
                }
            },
            canShow: (value, row) => true,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.hasVisibilityRules),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.hasVisibilityRules),
            seed: (row: Suppliers) => {
                return this.fluentTracker.tracker.getValue(row.id, columnName.hasVisibilityRules) ||
                    typeof row.hasVisibilityRules === 'boolean'
                    ? row.hasVisibilityRules?.toString()
                    : null
            }
        }
        visibilityRulesColumn.filter = {
            via: Via.DROPDOWN,
            values: galvanicTreatmentOptions,
            comparator: booleanComparator
        }

        const visibleToColumn = columnsMap.get(columnName.visibleTo)
        visibleToColumn.control = {
            delegate: (value, row) => this.delegates.visibleTo.get(row.id),
            type: TableControlType.SELECT,
            values: (value, row: Suppliers) => {
                const options = new Map<string, string>()
                if (!row.hasVisibilityRules) {
                    options.set('all', 'ALL')
                } else {
                    this.pendingSource.map(suppliers => {
                        if (suppliers.id !== row.id) {
                            options.set(suppliers.organizationId, `${suppliers.key} - ${suppliers.description}`)
                        }
                    })
                    if (Array.isArray(value)) {
                        value?.forEach(item => {
                            if ((item && !this.visibleToValue.get(item?.id)) || !row.hasVisibilityRules) {
                                options.set(item.id, item.code ? item.id + ' - ' + item.code : item.id)
                            }
                        })
                        return options
                    }
                }
                return options
            },
            multiple: true,
            change: (value, row) => {
                value = value?.map(valueItem => {
                    if (valueItem === 'all') {
                        return {
                            id: 'all',
                            code: 'ALL'
                        }
                    }
                    const find = this.pendingSource.find(item => item.organizationId === valueItem)
                    if (find) {
                        return {
                            id: find.organizationId,
                            code: find.key,
                            companyName: find.description
                        }
                    } else {
                        return row.visibleTo?.find(item => item.id === valueItem)
                    }
                })
                if (value?.length) {
                    this.updateFluentValue(value, row, columnName.visibleTo)
                } else {
                    this.updateFluentValue(null, row, columnName.visibleTo)
                }
            },
            canShow: (value, row) => true,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.visibleTo),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.visibleTo),
            seed: (row: Suppliers) => {
                return (
                    this.fluentTracker.tracker.getValue(row.id, columnName.visibleTo)?.map(item => item.id) ||
                    row.visibleTo?.map(item => item.id)
                )
            }
        }
        visibleToColumn.filter = {
            via: Via.SELFPICKER,
            selfValues: selfValuesFactory('visibleTo', libArrayValuesToMap),
            comparator: arrayComparatorFactory(v => (v ? v.map(item => item.id) : []))
        }

        const semifinishedColumn = columnsMap.get(columnName.semiFinishedSupplier)
        semifinishedColumn.control = {
            type: TableControlType.SELECT,
            values: new Map<string, string>([
                ['true', 'Yes'],
                ['false', 'No']
            ]),
            change: (value, row) => {
                if (value !== null) {
                    value = value === 'true'
                }
                this.updateFluentValue(value, row, columnName.semiFinishedSupplier)
            },
            canShow: (value, row) => true,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.semiFinishedSupplier),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.semiFinishedSupplier),
            seed: (row: Suppliers) => {
                return this.fluentTracker.tracker.getValue(row.id, columnName.semiFinishedSupplier) ||
                    typeof row.semiFinishedSupplier === 'boolean'
                    ? row.semiFinishedSupplier?.toString()
                    : null
            }
        }
        semifinishedColumn.filter = {
            via: Via.DROPDOWN,
            values: semifinishedSupplierOptions,
            comparator: booleanComparator
        }

        const createdAtColumn = columnsMap.get('createdAt')
//         createdAtColumn.pipe = v => (v ? moment(v).format(DD/MM/YYYY) : )
        createdAtColumn.filter = {
            via: Via.MOMENT
        }
        createdAtColumn.sorting = {
            sortable: true,
            compare: compareDates('createdAt')
        }

        const createdByColumn = columnsMap.get('createdBy')
        createdByColumn.filter = {
            via: Via.SELF,
            selfValues: selfValuesFactory('createdBy', stringToMap),
            path: 'createdBy'
        }

        const updatedAtColumn = columnsMap.get('updatedAt')
//         updatedAtColumn.pipe = v => (v ? moment(v).format(DD/MM/YYYY) : )
        updatedAtColumn.filter = {
            via: Via.MOMENT
        }
        updatedAtColumn.sorting = {
            sortable: true,
            compare: compareDates('updatedAt')
        }

        const updatedByColumn = columnsMap.get('updatedBy')
        updatedByColumn.filter = {
            via: Via.SELF,
            selfValues: selfValuesFactory('updatedBy', stringToMap),
            path: 'updatedBy'
        }

        const rankColumn = columnsMap.get(columnName.rank)
        rankColumn.control = {
            type: TableControlType.INPUT,
            changing: (value, row) => {
                const indexToUpdate = this.pendingSource.findIndex(mc => mc.id === row.id)
                if (indexToUpdate !== -1) {
                    this.pendingSource[indexToUpdate].rank = value
                }
                this.fluentTracker.update(row.id, columnName.rank, value)
                this.updateExistFields(row)
            },
            canShow: () => true,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.rank),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.rank),
            seed: row => {
                return this.fluentTracker.tracker.getValue(row.id, columnName.rank) || row[columnName.rank]
            }
        }
        rankColumn.sorting = {
            sortable: true
        }

        const disableColumn = columnsMap.get('disabled')
        disableColumn.control = {
            type: TableControlType.DROPDOWN,
            values: disabledColumnOptions,
            required: true,
            change: (value, row) => {
                this.fluentTracker.update(row.id, 'disabled', value)
                this.updateExistFields(row)
            },
            disabled: () => false,
            canShow: () => true,
            seed: row => this.fluentTracker.tracker.getValue(row.id, 'disabled') || row.disabled?.toString()
        }
        disableColumn.sorting = {
            sortable: false
        }
        disableColumn.filter = {
            via: Via.DROPDOWN,
            values: disabledColumnOptions,
            comparator: booleanComparator
        }
    }

    canTableRemove(): boolean {
        return true
    }

    get importTemplateUrl() {
        return `/libraries/management/types/supplier/import-entries/template.xlsx`
    }

    importFailed(error) {
        if (typeof error === 'string') {
            this.error = error
        } else {
            this.error = 'Unknown error'
        }
    }

    beforeImport() {
        this.error = null
        this.saved = false
    }

    afterImport(imported) {
        if (imported.length > 0) {
            const entriesByKey: { [key: string]: Entry } = {}
            this.table.getAllData().forEach(row => (entriesByKey[row.key] = row))

            let hasDuplicates = false
            let hasRankOverflow = false
            let hasNotIntRank = false
            imported.forEach(entry => {
                const found = entriesByKey[entry.key]
                if (found) {
                    hasDuplicates = true
                }
                if (entry.rank && !isZeroOrPositiveInt(entry.rank)) {
                    hasNotIntRank = true
                }
                if (entry.rank > maxRank) {
                    hasRankOverflow = true
                }

                const newItem = Object.assign({}, this.createNewSuppliers(), {
                    ...entry,
                    visibleTo: entry.visibleToSuppliers
                })
                this.pendingSource.push(newItem)
                this.updateExistFields(newItem)
                Object.keys(newItem).forEach(field => {
                    if (field === 'key') {
                        const company = this.getCompany(newItem.key)
                        const companyInfo = this.getCompanyInfo(company?.companyId)
                        this.updateFluentValue(newItem.key, newItem, columnName.key, false)
                        if (companyInfo) {
                            if (companyInfo.active) {
                                this.updateFluentValue(
                                    companyInfo.companyId,
                                    newItem,
                                    columnName.catalogUploadedBy,
                                    false
                                )
                            }
                            this.updateFluentValue(company.attributes?.sapCode, newItem, columnName.sapCode, false)
                            this.updateFluentValue(companyInfo.active, newItem, columnName.supplierStatus, false)
                            this.updateFluentValue(companyInfo.companyName, newItem, columnName.description, false)
                            this.updateFluentValue(company.companyId, newItem, 'organizationId', false)
                        } else {
                            this.fluentTracker.tracker.addError(newItem.id, columnName.key, 'not found')
                        }
                        return
                    }
                    if (field === 'disabled') {
                        this.fluentTracker.update(newItem.id, field, newItem[field]?.toString())
                        return
                    }
                    if (field === 'country') {
                        this.fluentTracker.update(newItem.id, columnName.country, newItem.country)
                        if (newItem.country === null) {
                            this.fluentTracker.tracker.addError(newItem.id, columnName.country, "It's required")
                        }
                        if (newItem.country?.id) {
                            const findCountry = this.countrySource.find(
                                country => newItem.country.id?.toLowerCase() === country.key?.toLowerCase()
                            )
                            if (!findCountry) {
                                this.fluentTracker.tracker.addError(
                                    newItem.id,
                                    columnName.country,
                                    im('{value} is not found', { value: LibraryHelper.libraryAttributeToStringOption(newItem.country)})
                                )
                            } else {
                                if (findCountry) {
                                    const CountryToLibraryAttribute = {
                                        id: findCountry.key,
                                        code: findCountry.description
                                    }
                                    this.updateFluentValue(
                                        CountryToLibraryAttribute,
                                        newItem,
                                        columnName.country,
                                        false
                                    )
                                }
                            }
                        }
                        return
                    }
                    if (field === 'visibleToSuppliers') {
                        if (newItem.hasVisibilityRules === false) {
                            newItem[field] = [
                                {
                                    id: 'all',
                                    code: 'ALL'
                                }
                            ]
                            this.updateFluentValue(newItem[field], newItem, columnName.visibleTo, false)
                            return
                        } else {
                            if (newItem[field]?.length) {
                                newItem[field] = newItem[field].map(item => {
                                    const findVtItem = this.pendingSource.find(
                                        mPItem => mPItem.key?.toLowerCase() === item.id?.toLowerCase()
                                    )
                                    if (findVtItem) {
                                        return {
                                            id: findVtItem?.organizationId,
                                            code: findVtItem?.key,
                                            companyName: findVtItem?.description
                                        }
                                    }
                                    return item
                                })
                                this.updateFluentValue(newItem[field], newItem, columnName.visibleTo, false)
                            } else {
                                this.updateFluentValue([], newItem, columnName.visibleTo, false)
                                this.fluentTracker.tracker.addError(newItem.id, columnName.visibleTo, "It's required")
                            }
                        }
                        if (newItem.visibleTo?.length && newItem.hasVisibilityRules) {
                            const errors = []
                            newItem.visibleTo.forEach(item => {
                                const findVt = this.pendingSource.find(
                                    vTItem => vTItem.key?.toLowerCase() === item.id?.toLowerCase()
                                )
                                if (!this.visibleToValue.get(item.id?.toLowerCase()) || (findVt?.disabled && item.id)) {
                                    if (findVt) {
                                        errors.push(
                                            findVt.description ? findVt.key + '-' + findVt.description : findVt.key
                                        )
                                    } else {
                                        errors.push(item.code ? item.id + '-' + item.code : item.id)
                                    }
                                }
                            })

                            if (errors.length) {
                                errors.push('not found')
                                this.fluentTracker.tracker.addError(newItem.id, columnName.visibleTo, errors)
                            }
                        } else if (!newItem.visibleTo?.length && newItem.hasVisibilityRules) {
                            this.fluentTracker.tracker.addError(newItem.id, columnName.visibleTo, "It's required")
                        }
                        return
                    }
                    if (field === 'types') {
                        if (newItem[field]?.length) {
                            newItem[field] = newItem[field].map(item => {
                                const findVtItem = this.supplierTypeSource.find(
                                    mPItem => mPItem.key?.toLowerCase() === item.id?.toLowerCase()
                                )
                                if (findVtItem) {
                                    return {
                                        id: findVtItem?.key,
                                        code: findVtItem?.description
                                    }
                                }
                                return item
                            })
                            this.updateFluentValue(newItem[field], newItem, columnName.types, false)
                        } else {
                            this.updateFluentValue([], newItem, columnName.types, false)
                            this.fluentTracker.tracker.addError(newItem.id, columnName.types, "It's required")
                        }
                        if (newItem.types?.length) {
                            const errors = []
                            newItem.types.forEach(item => {
                                const findVt = this.supplierTypeSource.find(
                                    vTItem => vTItem.key?.toLowerCase() === item.id?.toLowerCase()
                                )
                                if (
                                    !this.supplierTypeValue.get(item.id?.toLowerCase()) ||
                                    (findVt?.disabled && item.id)
                                ) {
                                    if (findVt) {
                                        errors.push(
                                            findVt.description ? findVt.key + '-' + findVt.description : findVt.key
                                        )
                                    } else {
                                        errors.push(item.code ? item.id + '-' + item.code : item.id)
                                    }
                                }
                            })

                            if (errors.length) {
                                errors.push('not found')
                                this.fluentTracker.tracker.addError(newItem.id, columnName.types, errors)
                            }
                        } else if (!newItem.types?.length) {
                            this.fluentTracker.tracker.addError(newItem.id, columnName.types, "It's required")
                        }
                        return
                    }
                    if (field === columnName.hasVisibilityRules) {
                        this.updateFluentValue(newItem[field], newItem, columnName.hasVisibilityRules)
                        if (typeof newItem[field] !== 'boolean') {
                            this.updateFluentValue(null, newItem, columnName.hasVisibilityRules)
                            this.fluentTracker.tracker.addError(newItem.id, 'hasVisibilityRules', "It's required")
                        }

                        return
                    }
                    if (field === columnName.semiFinishedSupplier) {
                        this.updateFluentValue(newItem[field], newItem, columnName.semiFinishedSupplier)
                        if (typeof newItem[field] !== 'boolean') {
                            this.updateFluentValue(null, newItem, columnName.semiFinishedSupplier)
                            this.fluentTracker.tracker.addError(newItem.id, 'semiFinishedSupplier', 'Invalid Value')
                        }
                        return
                    }
                    if (field === columnName.catalogUploadedBy) {
                        if (newItem[field] === null) {
                            this.fluentTracker.tracker.addError(
                                newItem.id,
                                columnName.catalogUploadedBy,
                                "It's required"
                            )
                        }
                        if (newItem[field]) {
                            const findC = this.organizationsSource.find(
                                item => item.companyId?.toLowerCase() === newItem[field].toLowerCase()
                            )
                            this.fluentTracker.update(newItem.id, columnName.catalogUploadedBy, newItem[field])
                            if (!findC) {
                                if (newItem[field].toLowerCase() === 'none') {
                                    return
                                } else {
                                    this.fluentTracker.tracker.addError(
                                        newItem.id,
                                        columnName.catalogUploadedBy,
                                        im('{value} is not found', { value: newItem[field] })
                                    )
                                }
                            } else {
                                const findOrganizationAttribute = {
                                    id: findC.companyId,
                                    code: findC.companyName
                                }
                                this.fluentTracker.update(
                                    newItem.id,
                                    columnName.catalogUploadedBy,
                                    findOrganizationAttribute
                                )
                                this.updateFluentValue(
                                    findOrganizationAttribute,
                                    newItem,
                                    columnName.catalogUploadedBy,
                                    false
                                )
                            }
                        }
                        return
                    }
                })
            })
            let error = []
            if (hasDuplicates) {
                error.push('The key must be unique.')
            }
            if (hasNotIntRank || hasRankOverflow) {
                error.push(im('Rank should be a positive number and less than {maxRank}.', { maxRank }))
            }
            if (error?.length) {
                error.push('Please check the data.')
            }
            if (this.fluentTracker.hasErrors()) {
                if (!error?.length) {
                    error.push('Some of the imported entries have errors. Please check the data.')
                }
                this.error = error
            }
            this.table.goToLastPage()
            this.table.reload()
        }
    }

    canTableExport(): boolean {
        return true
    }

    getTableExportTypes(): TableExportType[] {
        return [
            {
                name: 'xlsx',
                url: '/libraries/management/types/supplier/export-entries/xlsx'
            }
        ]
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mapBeforeTableExportDirectly(rows: any[]) {
        this.fluentTracker.validateAll()
        if (!this.fluentTracker.hasErrors()) {
            return rows.map(item => {
                const copy = JSON.parse(JSON.stringify(item))
                delete copy.__index
                delete copy.__internalRowNumbers
                delete copy.__sortIndex
                delete copy.new
                return this.convertObjectToSend(copy, false)
            })
        } else {
            return rows
                .map(item => {
                    const copy = JSON.parse(JSON.stringify(item))
                    delete copy.__index
                    delete copy.__internalRowNumbers
                    delete copy.__sortIndex
                    return this.convertObjectToSend(copy, false)
                })
                .filter(item => !item.new)
        }
    }

    generateTableExportFilename(type: TableExportType): string {
        return 'BC-' + moment().format('YYYY-MM-DD') + '-Handbook-Suppliers.xlsx'
    }

    shouldTableExportDirectly() {
        return true
    }

    configureTableRemoveAction(action: Action) {
        action.disabled = (row: Suppliers) => row.isUsed
        action.run = (row: Suppliers) => {
            return new Observable(observer => {
                const confirmation = this.confirmationService.openWithParams({
                    text: 'Are you sure that you want to delete this entry?',
                    title: 'Delete'
                })
                confirmation
                    .then(() => {
                        this.error = null
                        this.saved = false

                        if (row.new) {
                            this.removeItem(row.id)
                            return of(true)
                        }
                        delete row.__index
                        delete row.__internalRowNumbers
                        delete row.__sortIndex
                        row = this.convertObjectToSend(row)
                        return this.suppliersService.remove([row]).subscribe(
                            data => {
                                this.removeItem(row.id)
                                observer.next(data)
                            },
                            error => {
                                const arrayError = []
                                if (typeof error === 'object') {
                                    Object.entries(error).forEach(([key, value]) => {
                                        if (arrayError.length > 0) {
                                            arrayError.push(' , ')
                                        }
                                        arrayError.push(value)
                                    })
                                    this.error = arrayError.join('')
                                    this.saving = false
                                    return
                                }
                            }
                        )
                    })
                    .catch(() => {
                        observer.next(true)
                    })
            })
        }
    }

    canSave(): boolean {
        return this.fluentTracker.hasValues() && !this.fluentTracker.hasErrors() && !this.saving
    }

    private convertPayload(): { create?: Suppliers[]; update?: Suppliers[] } | [] {
        const fluentTracker = this.fluentTracker.generate()
        if (!this.fluentTracker.hasErrors()) {
            const data = {
                create: [],
                update: []
            }
            fluentTracker.forEach(item => {
                item = this.convertObjectToSend(item)
                if (item.new) {
                    delete item.new
                    data.create.push({ ...item })
                } else {
                    data.update.push({ ...item })
                }
            })
            return data
        } else {
            this.table.refresh()
            return []
        }
    }

    convertObjectToSend(fluentObject, deleteSupplierStatus = true): Suppliers {
        const copy = JSON.parse(JSON.stringify(fluentObject))
        if (!copy.hasVisibilityRules) {
            copy.visibleTo = []
            copy.visibleToSuppliers = []
        }
        if (copy.catalogUploadedBy === 'none') {
            copy.catalogUploadedBy = null
        }
        copy.disabled = copy.disabled === 'true'
        if (copy.visibleTo?.length) {
            copy.visibleToSuppliers = copy.visibleTo.map(
                (visibleToItem: LibraryAttributeWithDescription) => {
                    return {
                        id: visibleToItem.code,
                        code: visibleToItem.companyName
                    }
                }
            )
            copy.visibleTo = copy.visibleTo?.map(visibleItem => visibleItem.id)
        }
        if (copy.catalogUploadedBy?.id) {
            copy.catalogUploadedBy = copy.catalogUploadedBy?.id
        }
        if (deleteSupplierStatus) {
            delete copy.supplierStatus
        }
        return copy
    }

    save(): void {
        const data = this.convertPayload()
        this.error = null
        this.saved = false
        if (!Array.isArray(data)) {
            const { create, update } = data
            if (create.length) {
                this.saving = true
                this.suppliersService.create(create).subscribe(
                    response => {
                        this.pendingSource = []
                        this.table.reload(
                            new Subscriber(() => {
                                this.fluentTracker.clearAll()
                                this.saving = false
                                this.saved = true
                            })
                        )
                    },
                    error => {
                        this.saving = false
                        const arrayError = []
                        if (typeof error === 'object') {
                            Object.entries(error).forEach(([key, value]) => {
                                if (arrayError.length > 0) {
                                    arrayError.push(' , ')
                                }
                                arrayError.push(value)
                            })
                            this.error = arrayError.join('')
                            this.saving = false
                            return
                        }
                    }
                )
            }
            if (update.length) {
                this.saving = true
                this.suppliersService.update(update).subscribe(
                    response => {
                        this.pendingSource = []
                        this.table.reload(
                            new Subscriber(() => {
                                this.fluentTracker.clearAll()
                                this.saving = false
                                this.saved = true
                            })
                        )
                    },
                    error => {
                        const arrayError = []
                        if (typeof error === 'object') {
                            Object.entries(error).forEach(([key, value]) => {
                                if (arrayError.length > 0) {
                                    arrayError.push(' , ')
                                }
                                arrayError.push(value)
                            })
                            this.error = arrayError.join('')
                            this.saving = false
                            return
                        }
                        this.saving = false
                        this.error = error
                    }
                )
            }
        }
    }

    private createNewSuppliers(): Suppliers {
        const id = uuid()
        return {
            key: '',
            organizationId: '',
            sapCode: '',
            country: null,
            description: '',
            types: null,
            catalogUploadedBy: null,
            hasVisibilityRules: null,
            supplierStatus: false,
            visibleTo: null,
            disabled: false,
            rank: null,
            namespace: 'kering',
            library: 'supplier',
            id,
            new: true
        }
    }

    addNewItem(): void {
        const newItem = this.createNewSuppliers()
        this.pendingSource.splice(this.pendingSource.length + 1, 0, newItem)
        this.updateExistFields(newItem)
        this.table.reload()
        this.table.goToLastPage()
    }

    private removeItem(id: string) {
        this.fluentTracker.tracker.destroy(id)
        const removedIndex = this.pendingSource.findIndex(md => md.id === id)
        this.pendingSource.splice(removedIndex, 1)

        this.table.reload()
    }

    private updateExistFields(row: Suppliers): void {
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.key)) {
            this.fluentTracker.update(row.id, columnName.key, row.key)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.sapCode)) {
            this.fluentTracker.update(row.id, columnName.sapCode, row.sapCode)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.country) && row.country) {
            this.fluentTracker.update(row.id, columnName.country, row.country)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.description)) {
            this.fluentTracker.update(row.id, columnName.description, row.description)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.types) && row.types?.length) {
            this.fluentTracker.update(row.id, columnName.types, row.types)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.catalogUploadedBy) && row.catalogUploadedBy) {
            this.fluentTracker.update(row.id, columnName.catalogUploadedBy, row.catalogUploadedBy)
        }
        if (
            !this.fluentTracker.tracker.hasValue(row.id, columnName.hasVisibilityRules) &&
            typeof row.hasVisibilityRules === 'boolean'
        ) {
            this.fluentTracker.update(row.id, columnName.hasVisibilityRules, row.hasVisibilityRules)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.visibleTo) && row.visibleTo?.length) {
            this.fluentTracker.update(row.id, columnName.visibleTo, row.visibleTo)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'disabled')) {
            this.fluentTracker.update(row.id, 'disabled', row.disabled?.toString())
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'rank')) {
            this.fluentTracker.update(row.id, 'rank', row.rank)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'new')) {
            this.fluentTracker.update(row.id, 'new', row.new)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'library')) {
            this.fluentTracker.update(row.id, 'library', row.library)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'namespace')) {
            this.fluentTracker.update(row.id, 'namespace', row.namespace)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'organizationId')) {
            this.fluentTracker.update(row.id, 'organizationId', row.organizationId)
        }
    }

    hasTableErrorForRow(row) {
        return this.fluentTracker.hasErrors(row.id)
    }

    private updateFluentValue(value, row, column: string, isUpdateExistField = true) {
        const indexToUpdate = this.pendingSource.findIndex(mc => mc.id === row.id)
        if (indexToUpdate !== -1) {
            this.pendingSource[indexToUpdate][column] = value
            row[column] = value
        }
        this.fluentTracker.update(row.id, column, value)
        if (isUpdateExistField) {
            this.updateExistFields(row)
        }
    }

    private getCompany(vatCode: string): OrganizationInfo | undefined {
        if (!vatCode) {
            return null
        }
        return this.organizationsSource.find(organization => {
            if (organization.attributes?.vatCode === vatCode) {
                return true
            }
            return false
        })
    }

    private getCompanyInfo(companyId: string): OrganizationInfo | undefined {
        if (!companyId) {
            return null
        }
        return this.organizationsSource.find(organization => organization.companyId === companyId)
    }
}
